import { Grid } from '@design-systems/canvas-ui'
import { useState, useCallback } from 'react'
import { addMonths, subMonths } from 'date-fns'
import {
    getFormattedDateMonthandYear,
    getFormattedReportDate,
    getHeaderText,
    getLocationFlexHours,
    PAYROLL_TIME_DIMENSIONS,
} from '../helpers'
import { addHyperlinkToFirstColumn } from 'utils/data'
import { isStore as isStoreCheck } from 'utils/location'
import { Cell } from 'utils/data'
import { Container } from 'components/container/Container'
import { usePayrollLocationMonthly } from 'hooks/payroll/location'
import { NonStoreLocation } from './NonStoreLocation'
import { StoreLocation } from './StoreLocation'
import { GridData } from './GridData'
import useRefresh from 'hooks/utils/useRefresh'
import useCountdown from 'hooks/utils/useCountdown'
import { ErrorAlert } from 'components/error-fallback'
import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from '../../../locales/translation-keys'
import { translateTableData } from '../../../locales/utils'
import { GeneralizedComponent } from 'components/generalized-component/generalized-component'
import { useLogClick } from 'utils/analytics'

export const Location = () => {
    const { t, i18n, currentLocation, pathname, env, logout, session } =
        useEssentials()
    const { payroll } = TRANSLATION_KEYS
    const { location } = payroll
    const { logClick } = useLogClick('payroll')

    const isStore = isStoreCheck(currentLocation)
    // Index 1 = 'this_month' is the default. Toggle order: Last Month | This Month | Next Month
    const [activeTimePeriod, setActiveTimePeriod] = useState(1)
    const timeDimension = PAYROLL_TIME_DIMENSIONS[activeTimePeriod]
    const isNextMonth = timeDimension === 'next_month'

    const translatedTabs = [
        t(location.lastMonth),
        t(location.thisMonth),
        t(location.nextMonth),
    ]

    const handleTimePeriodChange = useCallback((index: number) => {
        setActiveTimePeriod(index)
    }, [])

    const baseFetchProps = {
        env,
        accessToken: session?.accessToken ?? '',
        location: currentLocation,
    }

    /* Monthly totals — all three fetched eagerly for instant tab switching */
    const {
        data: rawCurrentMonthly,
        isLoading: isCurrentMonthlyLoading,
        isError: isCurrentMonthlyError,
        refetch: refetchCurrentMonthly,
        remove: removeCurrentMonthly,
        error: currentMonthlyError,
    } = usePayrollLocationMonthly(baseFetchProps)

    const {
        data: rawPriorMonthly,
        isLoading: isPriorMonthlyLoading,
        isError: isPriorMonthlyError,
        refetch: refetchPriorMonthly,
        remove: removePriorMonthly,
        error: priorMonthlyError,
    } = usePayrollLocationMonthly({
        ...baseFetchProps,
        timeDimension: 'prior_month',
    })

    const {
        data: rawNextMonthly,
        isLoading: isNextMonthlyLoading,
        isError: isNextMonthlyError,
        refetch: refetchNextMonthly,
        remove: removeNextMonthly,
        error: nextMonthlyError,
    } = usePayrollLocationMonthly({
        ...baseFetchProps,
        timeDimension: 'next_month',
    })

    useRefresh(refetchCurrentMonthly, removeCurrentMonthly)
    useRefresh(refetchPriorMonthly, removePriorMonthly)
    useRefresh(refetchNextMonthly, removeNextMonthly)

    const rawData =
        timeDimension === 'prior_month'
            ? rawPriorMonthly
            : timeDimension === 'next_month'
              ? rawNextMonthly
              : rawCurrentMonthly
    const isLoading =
        timeDimension === 'prior_month'
            ? isPriorMonthlyLoading
            : timeDimension === 'next_month'
              ? isNextMonthlyLoading
              : isCurrentMonthlyLoading
    const isMonthlyError =
        timeDimension === 'prior_month'
            ? isPriorMonthlyError
            : timeDimension === 'next_month'
              ? isNextMonthlyError
              : isCurrentMonthlyError
    const monthlyError =
        timeDimension === 'prior_month'
            ? priorMonthlyError
            : timeDimension === 'next_month'
              ? nextMonthlyError
              : currentMonthlyError
    const refetch =
        timeDimension === 'prior_month'
            ? refetchPriorMonthly
            : timeDimension === 'next_month'
              ? refetchNextMonthly
              : refetchCurrentMonthly

    const count = useCountdown(5, logout, monthlyError as Error)

    let projectedFlexHoursVal: Cell = { value: 'n/a' },
        trailerFlexHoursVal: Cell = { value: 'n/a' },
        adjustmentsVal: Cell = { value: 'n/a' }

    if (isMonthlyError) {
        return (
            <ErrorAlert
                error={monthlyError}
                count={count}
                onRetry={() => refetch()}
            />
        )
    }

    let data = rawData
    if (data) {
        data = addHyperlinkToFirstColumn(data, currentLocation, pathname)
        // For next month, omit the actual + remaining hours column — it is only available for past/present months.
        // For store monthly view, 'act+rem sched hrs' maps to the scheduled hrs column which
        // IS available for next month (it represents scheduled hours). The API column name
        // 'act+rem sched hrs' doubles as the scheduled hrs column for future months.
        const sharedTranslation = {
            loc: location.loc,
            location: location.location,
            'plan hrs': payroll.planHrs,
            variance: location.variance,
        }
        const translation = isStore
            ? {
                  ...sharedTranslation,
                  'act+rem sched hrs': payroll.actRemSchedHrs,
              }
            : isNextMonth
              ? {
                    ...sharedTranslation,
                    'other adjustments': location.otherAdjustments,
                }
              : {
                    ...sharedTranslation,
                    'Actual + Remaining Wkly': payroll.actRemSchedHrs,
                    'other adjustments': location.otherAdjustments,
                }
        data = translateTableData({
            t,
            table: data,
            translation,
        })
        const { footer } = data
        if (footer && !isNextMonth) {
            const { projectedFlexHours, trailerFlexHours, adjustmentsHours } =
                getLocationFlexHours(currentLocation, footer)
            projectedFlexHoursVal = projectedFlexHours
            trailerFlexHoursVal = trailerFlexHours
            adjustmentsVal = adjustmentsHours
        }
        if (footer && isNextMonth) {
            const { adjustmentsHours } = getLocationFlexHours(
                currentLocation,
                footer,
            )
            adjustmentsVal = adjustmentsHours
        }
    }

    const displayDate =
        timeDimension === 'prior_month'
            ? subMonths(new Date(), 1)
            : timeDimension === 'next_month'
              ? addMonths(new Date(), 1)
              : new Date()
    const yearMonthDate = getFormattedDateMonthandYear(
        i18n.language,
        displayDate,
    )

    return (
        <Container className="hc8-pt-4x">
            <p className="hc8-fs-050 hc8-ma-none">
                {t(getHeaderText(currentLocation))}
            </p>
            <p className="hc8-fs-050 hc8-ma-none2">
                {t(location.asOf)} {getFormattedReportDate(i18n.language)}
            </p>
            <Grid.Container
                align="center"
                justify="center"
                className="hc8-mb-3x hc8-mt-2x"
            >
                <Grid.Item>
                    <GeneralizedComponent.Buttons
                        items={translatedTabs}
                        selected={activeTimePeriod}
                        setSelected={handleTimePeriodChange}
                        className="data-tabs"
                        kpiName="payroll-time-period"
                        logClick={logClick}
                    />
                </Grid.Item>
            </Grid.Container>
            <GridData
                isLoading={isLoading}
                isNextMonth={isNextMonth}
                data={[
                    projectedFlexHoursVal,
                    trailerFlexHoursVal,
                    adjustmentsVal,
                ]}
            />
            {!isStore && (
                <NonStoreLocation
                    data={data}
                    isLoading={isLoading}
                    currentLocation={currentLocation}
                    bottomLeftCell={yearMonthDate}
                    count={count}
                    isNextMonth={isNextMonth}
                />
            )}
            {isStore && (
                <StoreLocation
                    isLoading={isLoading}
                    currentLocation={currentLocation}
                    bottomLeftCell={yearMonthDate}
                    count={count}
                    activeTimePeriod={activeTimePeriod}
                />
            )}
        </Container>
    )
}
